import { ApiError, api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AccumulatedCostResponse,
  AnimalDetail,
  AnimalHistoryResponse,
  AnimalsListResponse,
  LifecycleState,
} from "../types";

// TODO: server-side pagination cuando un hato supere 1000 animales
export function useAnimalsList(accountId: string) {
  return useQuery({
    queryKey: ["animals", accountId],
    queryFn: () => api.get<AnimalsListResponse>(`/accounts/${accountId}/animals`),
    enabled: Boolean(accountId),
  });
}

export function useAnimal(accountId: string, animalId: string) {
  return useQuery({
    queryKey: ["animal", accountId, animalId],
    queryFn: () => api.get<AnimalDetail>(`/accounts/${accountId}/animals/${animalId}`),
    enabled: Boolean(accountId) && Boolean(animalId),
    retry: (failureCount, error) => {
      // No reintentar 404 — el animal no existe o no pertenece a la cuenta.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

// ─── Single-animal registration (B3) ─────────────────────────────────────────

type RegisterAnimalInput = {
  tag: string;
  name?: string;
  sex: "female" | "male";
  birthDate: string;
  birthDateIsEstimated: boolean;
  lotId: string;
  acquisitionType: "born" | "purchased";
  initialCost?: string;
  lifecycleState?: LifecycleState;
  paymentMethod?: "cash" | "credit";
};

type RegisterAnimalResponse = {
  animal: AnimalDetail;
};

export function useRegisterAnimal(accountId: string) {
  const qc = useQueryClient();
  return useMutation<RegisterAnimalResponse, Error, RegisterAnimalInput>({
    mutationFn: (input) =>
      api.post<RegisterAnimalResponse>(`/accounts/${accountId}/animals`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["animals", accountId] });
      void qc.invalidateQueries({ queryKey: ["lots", accountId] });
    },
  });
}

type ChangeAnimalLotInput = {
  fromLotId: string;
  toLotId: string;
  reason?: string;
};

export function useChangeAnimalLot(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangeAnimalLotInput) =>
      api.post(`/accounts/${accountId}/animals/${animalId}/events`, {
        eventType: "lot_changed",
        occurredAt: new Date().toISOString(),
        payload: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["animal", accountId, animalId] });
      void qc.invalidateQueries({ queryKey: ["animals", accountId] });
      void qc.invalidateQueries({ queryKey: ["lots", accountId] });
    },
  });
}

// ─── Operational events (Slice E) ────────────────────────────────────────────

function invalidateAnimalQueries(
  qc: ReturnType<typeof useQueryClient>,
  accountId: string,
  animalId: string,
) {
  void qc.invalidateQueries({ queryKey: ["animal", accountId, animalId] });
  void qc.invalidateQueries({ queryKey: ["animals", accountId] });
}

type RecordCalvingInput = {
  occurredAt: string;
  lactationNumber: number;
};

export function useRecordCalving(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ occurredAt, lactationNumber }: RecordCalvingInput) =>
      api.post(`/accounts/${accountId}/animals/${animalId}/events`, {
        eventType: "calving_recorded",
        occurredAt,
        payload: { lactationNumber },
      }),
    onSuccess: () => invalidateAnimalQueries(qc, accountId, animalId),
  });
}

// Frontera contable NIIF Sec 34 §4.5: el backend setea firstCalvingAt, transiciona
// a cow_producing+lactating, capitaliza el accumulated_cost a Vacas en producción
// y resuelve usefulLifeMonths/residualValue desde defaults de la cuenta.
// calfBorn es required en firstCalvingRecordedPayloadSchema; el dialog lo manda
// true por default (caso stillborn requiere correctEvent posterior).
type RecordFirstCalvingInput = {
  occurredAt: string;
  calfBorn?: boolean;
};

export function useRecordFirstCalving(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ occurredAt, calfBorn = true }: RecordFirstCalvingInput) =>
      api.post(`/accounts/${accountId}/animals/${animalId}/events`, {
        eventType: "first_calving_recorded",
        occurredAt,
        payload: { calfBorn },
      }),
    onSuccess: () => {
      invalidateAnimalQueries(qc, accountId, animalId);
      // Frontera contable: el cronograma de depreciación se activa con el
      // evento — invalidar vistas agregadas para que reflejen el cambio.
      void qc.invalidateQueries({ queryKey: ["depreciation"] });
      void qc.invalidateQueries({ queryKey: ["animal", accountId, animalId, "accumulated-cost"] });
    },
  });
}

type RecordDryOffInput = {
  occurredAt: string;
};

export function useRecordDryOff(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ occurredAt }: RecordDryOffInput) =>
      api.post(`/accounts/${accountId}/animals/${animalId}/events`, {
        eventType: "dry_off_recorded",
        occurredAt,
        payload: {},
      }),
    onSuccess: () => invalidateAnimalQueries(qc, accountId, animalId),
  });
}

type TransitionStageInput = {
  occurredAt: string;
  from: LifecycleState;
  to: LifecycleState;
  reason?: string;
};

export function useTransitionStage(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ occurredAt, from, to, reason }: TransitionStageInput) =>
      api.post(`/accounts/${accountId}/animals/${animalId}/events`, {
        eventType: "stage_transitioned",
        occurredAt,
        payload: reason ? { from, to, reason } : { from, to },
      }),
    onSuccess: () => invalidateAnimalQueries(qc, accountId, animalId),
  });
}

// ─── Termination event (B7) ─────────────────────────────────────────────────
// Tres razones, mismo endpoint, payload distinto. La validación dura del payload
// está en el backend (registerEventSchema). El form valida reglas de UX (campos
// obligatorios, fechas) y este hook solo arma el body en el shape que espera el
// backend (price como string decimal, no Number).

export type RegisterTerminationInput =
  | { reason: "died"; occurredAt: string; cause: string }
  | { reason: "culled"; occurredAt: string; reasonText?: string }
  | {
      reason: "sold";
      occurredAt: string;
      price: string;
      buyer?: string;
      // B67 (facturas-v1 §3.2): datos de la factura emitida por la venta.
      invoiceNumber?: string;
      buyerId?: string;
      saleCondition?: "contado" | "credito";
    };

export function useRegisterTermination(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterTerminationInput) => {
      const path = `/accounts/${accountId}/animals/${animalId}/events`;
      const occurredAt = input.occurredAt;
      if (input.reason === "died") {
        return api.post(path, {
          eventType: "animal_died",
          occurredAt,
          payload: { cause: input.cause },
        });
      }
      if (input.reason === "culled") {
        const payload: { reason?: string } = {};
        if (input.reasonText) payload.reason = input.reasonText;
        return api.post(path, { eventType: "animal_culled", occurredAt, payload });
      }
      const payload: {
        price: string;
        buyer?: string;
        currencyCode: "CRC";
        invoice_number?: string;
        buyer_id?: string;
        sale_condition?: "contado" | "credito";
      } = {
        price: input.price,
        currencyCode: "CRC",
      };
      if (input.buyer) payload.buyer = input.buyer;
      // B67: snake_case en el payload del evento (espejo del spec §3.2).
      if (input.invoiceNumber) payload.invoice_number = input.invoiceNumber;
      if (input.buyerId) payload.buyer_id = input.buyerId;
      if (input.saleCondition) payload.sale_condition = input.saleCondition;
      return api.post(path, { eventType: "animal_sold", occurredAt, payload });
    },
    onSuccess: () => invalidateAnimalQueries(qc, accountId, animalId),
  });
}

// ─── Timeline + accumulated cost (Slice G) ───────────────────────────────────

// El endpoint usa cursor-based pagination. No hay useInfiniteQuery en el repo;
// el caller mantiene `cursor` en estado local y va concatenando páginas. La
// queryKey incluye el cursor para que cada página se cachee independientemente.
export function useAnimalHistory(
  accountId: string,
  animalId: string,
  cursor: string | null,
  opts?: { limit?: number },
) {
  const limit = opts?.limit ?? 50;
  return useQuery({
    queryKey: ["animal", accountId, animalId, "history", cursor, limit],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor) params.set("cursor", cursor);
      return api.get<AnimalHistoryResponse>(
        `/accounts/${accountId}/animals/${animalId}/history?${params.toString()}`,
      );
    },
    enabled: Boolean(accountId) && Boolean(animalId),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useAccumulatedCost(accountId: string, animalId: string, atDate: string) {
  return useQuery({
    queryKey: ["animal", accountId, animalId, "accumulated-cost", atDate],
    queryFn: () =>
      api.get<AccumulatedCostResponse>(
        `/accounts/${accountId}/animals/${animalId}/accumulated-cost?at=${atDate}`,
      ),
    enabled: Boolean(accountId) && Boolean(animalId) && Boolean(atDate),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return false;
      return failureCount < 2;
    },
  });
}

// ─── Sprint 3 (2026-05-17): pesos del animal ────────────────────────────────

export type WeightSource = "balance" | "tape";

export type WeightRecord = {
  id: string;
  measuredAt: string;
  weightKg: string;
  source: WeightSource;
  notes: string | null;
  recordedBy: string;
  createdAt: string;
};

export type WeightHistoryResponse = { records: WeightRecord[] };

export function useWeightHistory(accountId: string, animalId: string) {
  return useQuery({
    queryKey: ["animal", accountId, animalId, "weights"],
    queryFn: () =>
      api.get<WeightHistoryResponse>(`/accounts/${accountId}/animals/${animalId}/weights`),
    enabled: Boolean(accountId) && Boolean(animalId),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export type RecordWeightInput = {
  measuredAt: string;
  weightKg: string;
  source: WeightSource;
  notes?: string;
};

export function useRecordWeight(accountId: string, animalId: string) {
  const qc = useQueryClient();
  return useMutation<WeightRecord, Error, RecordWeightInput>({
    mutationFn: (input) =>
      api.post<WeightRecord>(`/accounts/${accountId}/animals/${animalId}/weights`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["animal", accountId, animalId, "weights"] });
    },
  });
}
